<template>
  <article
    data-qa="analyses_lpu_card"
    class="lpu-card w-100"
    :class="{
      'lpu-card--special-placement pa-2 pa-sm-3': showPerCardSpecialPlacement,
      'lpu-card--ultima': isUltima && !isSpecialPlacement && context.request.feature_flags.allow_ultima,
      'bg-gray-0': !showPerCardSpecialPlacement && !isUltima,
    }"
    :data-qa-lpu-id="id"
    :data-schedule-item="scheduleItemJson"
    :data-club-is-enabled-lpu="isClubDiscountEnabled ? 'true' : 'false'"
    :data-special-placement="isSpecialPlacement ? '1' : undefined"
  >
    <div
      v-if="showPerCardSpecialPlacement"
      data-qa="analyses_lpu_card_special_label"
      class="lpu-card__special-label text-caption text-center text-no-wrap"
    >
      Спецразмещение
    </div>

    <div
      v-else-if="isUltima && context.request.feature_flags.allow_ultima"
      data-qa="analyses_lpu_card_ultima_label"
      class="lpu-card__ultima-label d-flex align-center pl-4 text-caption text-no-wrap"
    >
      Ультима
    </div>

    <div
      class="lpu-card__body d-flex flex-column flex-sm-row align-center align-sm-start ga-4 w-100 min-w-0 pa-4 pa-sm-6"
      :class="{ 'bg-gray-0': showPerCardSpecialPlacement || (isUltima && context.request.feature_flags.allow_ultima) }"
    >
      <div class="lpu-card__aside">
        <a
          :href="href"
          class="lpu-card__logo-wrap lpu-card__order-logo position-relative w-100"
        >
          <LpuLogoPicture
            v-if="ff_update_lpu_logo && !closed"
            img-class="lpu-card__logo"
            data-qa="analyses_lpu_card_logo"
            contain
            :files="logoFiles"
            :alt="logoAlt"
          />
          <img
            v-else-if="ff_update_lpu_logo"
            data-qa="analyses_lpu_card_logo"
            class="lpu-card__logo"
            :src="logoUrl"
            :alt="logoAlt"
          >
          <v-img
            v-else
            data-qa="analyses_lpu_card_logo"
            :src="logoUrl"
            :alt="logoAlt"
            class="lpu-card__logo"
            contain
          />
        </a>

        <div
          v-if="!closed"
          class="lpu-card__meta lpu-card__order-meta"
        >
          <LpuCardRating
            :rating="rating"
            :reviews-count="reviewsCount"
            :reviews-href="reviewsHref"
          />
        </div>

        <div
          v-if="isAwardWinner || lpuLoyaltyProgram"
          class="lpu-card__badges lpu-card__order-badges d-flex flex-column align-center ga-2 w-100"
        >
          <AwardWinner
            v-if="isAwardWinner"
            type="lpu"
            :redirect-url="awardRedirectUrl || lpuPageHref"
            :current-award-year="currentAwardYear || 0"
            :is-award-or-grand-prix-winner="Boolean(isAwardWinner)"
          />

          <LpuLoyaltyProgram
            v-if="lpuLoyaltyProgram"
            :bonus-levels="lpuLoyaltyProgram.levels"
            :max-payment-percent="lpuLoyaltyProgram.max_payment_percent"
            use-label
          />
        </div>
      </div>

      <div class="lpu-card__main">
        <div class="lpu-card__head lpu-card__order-head d-flex flex-column align-center align-sm-start ga-1 min-w-0 text-center text-sm-left">
          <div class="lpu-card__name text-h6">
            <a
              data-qa="analyses_lpu_card_name"
              :href="href"
              class="text--text text-decoration-none"
            >
              {{ name }}
            </a>
            <LpuLicenseBadge
              v-if="license"
              qa="analyses_lpu_card_verified_badge"
              :text="`Лицензия клиники: ${license}`"
            />
          </div>

          <div
            data-qa="analyses_lpu_card_type"
            class="text-body-2 text-secondary--text"
          >
            {{ lpuTypeName }}
          </div>

          <div
            v-if="closed"
            data-qa="analyses_lpu_card_closed_alert"
            class="lpu-card__closed-alert d-inline-flex align-center gc-2 py-2 px-3 rounded bg-warning"
          >
            <span class="ui-icon-circle-warning ui-icon-fz-medium flex-shrink-0" />
            <span class="text-body-2 text--text">
              Клиника больше не работает
            </span>
          </div>
        </div>

        <div class="lpu-card__content lpu-card__order-content d-flex flex-column ga-4 min-w-0 w-100 text-left">
          <div class="d-flex flex-column ga-3">
            <div
              v-if="analysesCount"
              class="d-flex align-center gc-2"
            >
              <span class="ui-icon-laba ui-icon-fz-medium icon-secondary--text flex-shrink-0" />
              <span
                data-qa="analyses_lpu_card_analyses_count"
                class="text-body-2 text--text"
              >
                {{ analysesCountText }}
              </span>
            </div>

            <div class="d-flex align-start gc-2">
              <span class="ui-icon-map-pin ui-icon-fz-medium icon-secondary--text flex-shrink-0" />
              <span
                data-qa="analyses_lpu_card_address"
                class="text-body-2 text--text"
              >
                {{ address }}
              </span>
            </div>

            <div
              v-if="metros.length"
              class="lpu-card__metros d-flex flex-column ga-2"
            >
              <div
                v-for="(metro, index) in metros"
                :key="`${metro.name}-${index}`"
                data-qa="analyses_lpu_card_metro"
                class="d-inline-flex align-center gc-2"
              >
                <span
                  class="lpu-card__metro-dot flex-shrink-0"
                  :style="{ backgroundColor: metro.color }"
                />
                <span class="text-caption text--text">
                  {{ metro.name }}
                </span>
                <span class="text-caption text-secondary--text">
                  {{ metro.distance }}
                </span>
              </div>
            </div>

            <div
              v-if="scheduleText"
              class="d-flex align-start gc-2"
            >
              <span class="ui-icon-clock ui-icon-fz-medium primary--text flex-shrink-0" />
              <div>
                <LpuCardSchedule
                  :schedule-text="scheduleText"
                  :address-id="scheduleAddressId"
                />
                <div
                  v-if="closingTimeText"
                  data-qa="analyses_lpu_card_closing_time"
                  class="text-body-2 text-warning--text"
                >
                  {{ closingTimeText }}
                </div>
              </div>
            </div>

            <div
              v-if="phone"
              class="d-flex align-center gc-2"
            >
              <span
                class="ui-icon-phone-call ui-icon-fz-medium icon-secondary--text flex-shrink-0"
                :class="isMobile ? 'primary--text' : ''"
              />
              <a
                v-if="isMobile"
                data-qa="analyses_lpu_card_phone"
                :href="`tel:${phone}`"
                class="text-body-2 primary--text text-decoration-none"
              >
                {{ phone }}
              </a>
              <span
                v-else
                data-qa="analyses_lpu_card_phone"
                class="text-body-2 text--text"
              >
                {{ phone }}
              </span>
            </div>
          </div>

          <div
            v-if="isActiveClubDiscount && hasWindow"
            data-qa="analyses_lpu_card_club_discount"
            class="vue-club-service-discount-control"
          >
            <component :is="ClubServiceDiscountControl" />
          </div>

          <div
            class="b-card-services__calendar-selects"
            data-qa="lpu_card_calendar_selects"
          />

          <div
            v-if="isSuperuser && bidPlacement?.adminUrl"
            data-qa="analyses_lpu_card_sp_links"
          >
            <a
              :href="bidPlacement.adminUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="adminka"
            >
              Ставка({{ bidPlacement.bid }})
              - Место({{ bidPlacement.place }})
              - Оплата({{ bidPlacement.paymentSumma ?? '-' }})
            </a>
          </div>

          <div
            v-if="reviewsSummary"
            data-qa="analyses_lpu_card_reviews_summary"
            class="lpu-card__reviews-summary d-flex flex-column gr-1 pa-3 px-4 rounded-lg bg-secondary"
          >
            <div class="d-flex align-start gc-1">
              <span class="ui-icon-message-circle-fill ui-icon-fz-medium primary--text flex-shrink-0" />
              <span class="text-subtitle-2 text--text">
                Цитата из отзыва
              </span>
            </div>

            <p class="text-body-2 text-secondary--text mb-0">
              {{ reviewsSummary }}
            </p>
          </div>

          <div
            v-if="hasPriceItems"
            data-qa="analyses_lpu_card_prices"
            class="lpu-card__prices"
          >
            <div data-qa="service_card_first_three_items">
              <div
                v-for="(item, index) in visiblePriceItems"
                :key="`price-visible-${index}`"
                data-qa="service_card_price_item"
                class="d-flex justify-space-between mt-2"
              >
                <span class="text-body-2 text-break text--text">
                  {{ item.name }}
                </span>
                <span class="text-body-2 text-secondary--text ml-6 text-no-wrap">
                  <template v-if="item.withPreposition">от&nbsp;</template>{{ item.price }}
                </span>
              </div>
            </div>

            <template v-if="hiddenPriceItems.length">
              <button
                v-if="!isPricesExpanded"
                type="button"
                data-qa="analyses_lpu_card_prices_expand"
                class="lpu-card__prices-expand text-body-2 text-decoration-underline text-secondary--text mt-1"
                @click="isPricesExpanded = true"
              >
                Все цены ({{ priceItemsTotal }})
              </button>

              <div
                v-show="isPricesExpanded"
                data-qa="service_card_price_list"
              >
                <div
                  v-for="(item, index) in hiddenPriceItems"
                  :key="`price-hidden-${index}`"
                  data-qa="service_card_price_item"
                  class="d-flex justify-space-between mt-2"
                >
                  <span class="text-body-2 text-break text--text">
                    {{ item.name }}
                  </span>
                  <span class="text-body-2 text-secondary--text ml-6 text-no-wrap">
                    <template v-if="item.withPreposition">от&nbsp;</template>{{ item.price }}
                  </span>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, ref } from 'vue'
import { useContextStore } from 'src/store'
import type { ContextStore } from 'src/components/pages/info/legal-case/types'
import AwardWinner from 'src/components/common/AwardWinner/AwardWinner.vue'
import LpuCardRating from 'src/components/pages/[town-translit]/diagnostika/analiz/components/LpuCardRating.vue'
import LpuLicenseBadge from 'src/components/pages/[town-translit]/diagnostika/analiz/components/LpuLicenseBadge.vue'
import LpuCardSchedule from 'src/components/pages/[town-translit]/diagnostika/analiz/components/LpuCard/components/LpuCardSchedule.vue'
import { LpuCardsPageLoyaltyProgram } from 'src/components/pages/[town-translit]/diagnostika/analiz/_types'
import { formatLpuCardAnalysesCountText } from 'src/components/pages/[town-translit]/diagnostika/analiz/_functions/formatLpuCardTexts'
import LpuLoyaltyProgram from 'src/components/common/LpuLoyaltyProgram/LpuLoyaltyProgram.vue'
import LpuLogoPicture from 'src/components/common/LpuLogoPicture/LpuLogoPicture.vue'
import type { LpuLogoImageFile } from 'src/utils/getLpuAvatarUrl'

const { context }: ContextStore = useContextStore()
const hasWindow = typeof window !== 'undefined'
const isMobile = context.flavour === 'mobile'
const ff_update_lpu_logo = computed(() => Boolean(context.request.feature_flags.ff_update_lpu_logo))
const ff_update_spec_design_lpus = computed(() => Boolean(context.request?.feature_flags?.ff_update_spec_design_lpus))

const ClubServiceDiscountControl = defineAsyncComponent(
  () => import('www/vue_components/common/Club/components/core/ClubServiceDiscountControl/ClubServiceDiscountControl.vue'),
)

type LpuMetro = {
  name: string
  distance: string
  color: string
}

type LpuPriceItem = {
  name: string
  price: string
  withPreposition?: boolean
}

type LpuBidPlacement = {
  bid: number
  place: number
  paymentSumma?: number | null
  adminUrl?: string
}

type Props = {
  id: number | string
  name: string
  href: string
  lpuPageHref: string
  lpuTypeName: string
  logoUrl: string
  logoFiles?: LpuLogoImageFile[]
  logoAlt: string
  rating: number
  reviewsCount: number
  reviewsHref: string
  analysesCount: number
  lpuLoyaltyProgram?: LpuCardsPageLoyaltyProgram
  address: string
  metros: LpuMetro[]
  scheduleText: string
  scheduleAddressId?: number
  phone?: string
  reviewsSummary?: string
  closed?: boolean
  isSpecialPlacement?: boolean
  isUltima?: boolean
  isClubDiscountEnabled?: boolean
  scheduleItemJson?: string
  hasVideoCard?: boolean
  isAwardWinner?: boolean
  awardRedirectUrl?: string
  currentAwardYear?: number
  closingTimeText?: string
  license?: string
  isActiveClubDiscount?: boolean
  priceItems?: LpuPriceItem[]
  bidPlacement?: LpuBidPlacement | null
  isSuperuser?: boolean
}

const props = defineProps<Props>()

const showPerCardSpecialPlacement = computed(() => (
  Boolean(props.isSpecialPlacement) && !ff_update_spec_design_lpus.value
))

const isPricesExpanded = ref(false)
const PRICE_VISIBLE_COUNT = 3

const analysesCountText = computed(() => formatLpuCardAnalysesCountText(props.analysesCount))

const priceItemsList = computed(() => props.priceItems || [])
const visiblePriceItems = computed(() => priceItemsList.value.slice(0, PRICE_VISIBLE_COUNT))
const hiddenPriceItems = computed(() => priceItemsList.value.slice(PRICE_VISIBLE_COUNT))
const hasPriceItems = computed(() => priceItemsList.value.length > 0)
const priceItemsTotal = computed(() => priceItemsList.value.length)
</script>

<style lang="scss" scoped>
@import 'src/styles/variables';
@import 'src/styles/mixins';

.lpu-card {
  border-radius: 24px;
  box-shadow:
    0 2px 24px 0 rgba(9, 42, 72, 0.08),
    0 0 2px 0 rgba(21, 40, 92, 0.06),
    0 1px 2px 0 rgba(5, 23, 40, 0.08);

  &--special-placement {
    position: relative;
    background-color: $ui-kit-bg-warning;
  }

  &__special-label {
    position: absolute;
    top: 12px;
    left: 50%;
    z-index: 3;
    padding: 0 16px 4px;
    transform: translate(-50%, -50%);
    background-color: $ui-kit-bg-warning;
    color: $ui-kit-text-warning;
    border-radius: 4px 4px 16px 16px;
  }

  &__aside,
  &__main {
    display: contents;
  }

  &__order-logo {
    order: 1;
  }

  &__order-head {
    order: 2;
  }

  &__order-meta {
    order: 3;
  }

  &__order-badges {
    order: 4;
  }

  &__order-content {
    order: 5;
  }

  &__logo-wrap {
    width: 100%;
    max-width: 240px;

    :deep(img) {
      width: 100%;
      max-width: 240px;
      height: 80px;
      object-fit: contain;
    }
  }

  &__name {
    max-width: 100%;
  }

  &__logo {
    width: 100%;
    max-width: 240px;
    height: 80px;
    object-fit: contain;
  }

  &__favourite {
    top: -8px;
    right: -8px;
    z-index: 1;
    border-radius: $border-radius-lg;
  }

  &__metros {
    padding-left: 28px;
  }

  &__metro-dot {
    width: 8px;
    height: 8px;
    border-radius: $border-radius-circle;
  }

  &__prices-expand {
    display: block;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
    text-align: left;
  }

  @include media-breakpoint-up(sm) {
    &__aside,
    &__main {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    &__aside {
      align-items: center;
      flex-shrink: 0;
      width: 100%;
      max-width: 240px;
    }

    &__main {
      align-items: flex-start;
      flex-grow: 1;
      min-width: 0;
      width: 100%;
    }

    &__order-logo,
    &__order-head,
    &__order-meta,
    &__order-badges,
    &__order-content {
      order: initial;
    }
  }
}

.lpu-card--ultima {
  position: relative;
  padding: 0 4px 4px;
  border-bottom-left-radius: 28px;
  border-bottom-right-radius: 28px;
  background-image: $ultima-linear-gradient;

  &::after {
    content: '';
    width: 145px;
    height: 80px;
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1;
    background: url('/static/_v1/pd/pictures/ultima/border.png') no-repeat 0 0 / contain;
  }
}

.lpu-card__ultima-label {
  height: 32px;
  color: $ui-kit-bg-gray-0;
}

.lpu-card__body  {
  z-index: 2;
  position: relative;
  border-radius: 24px;
}
</style>
